import { useCallback, useMemo } from 'react';
import type { InternalTableSchema } from '../core/schema/types';

interface UseTableEditingParams {
  internalSchema: InternalTableSchema;
  tableState: {
    editingRows: Map<string, Record<string, unknown>>;
    editErrors: Map<string, Record<string, string>>;
    serverErrors: Map<string, Record<string, string>>;
  };
  stateActions: {
    startEditing: (rowKey: string, initialValues: Record<string, unknown>) => void;
    startEditingBatch: (entries: Array<{ rowKey: string; initialValues: Record<string, unknown> }>) => void;
    updateEditValue: (rowKey: string, field: string, value: unknown) => void;
    cancelEditing: (rowKey: string) => void;
    cancelAllEditing: () => void;
    setRowEditErrors?: (rowKey: string, errors: Record<string, string>) => void;
  };
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  applyComputeRules: (row: Record<string, unknown>, changedField: string) => Record<string, unknown>;
  patchEditedRow?: (
    row: Record<string, unknown>,
    context: { field: string; value: unknown },
  ) => Record<string, unknown>;
  validateEditedRow?: (row: Record<string, unknown>) => Record<string, string>;
}

interface UseTableEditingReturn {
  buildWholeRowEditEntries: (rowKey: string, row: Record<string, unknown>) => Array<{ rowKey: string; initialValues: Record<string, unknown> }>;
  getSubRowKeysForParent: (parentKey: string) => string[];
  handleCellDoubleClick: (rowKey: string, field: string) => void;
  handleEditChange: (rowKey: string, field: string, value: unknown) => void;
  handleEditBlur: (rowKey: string, field: string) => void;
  subRowDataField: string | undefined;
  handleSubRowCellDoubleClick: (parentKey: string, subIndex: number, field: string) => void;
  handleSubRowEditChange: (parentKey: string, subIndex: number, field: string, value: unknown) => void;
  handleSubRowEditBlur: (parentKey: string, subIndex: number, field: string) => void;
  editingSubRows: Map<string, Record<string, unknown>>;
  subRowEditErrors: Map<string, Record<string, string>>;
}

export function useTableEditing({
  internalSchema,
  tableState,
  stateActions,
  displayData,
  resolveRowKey,
  applyComputeRules,
  patchEditedRow,
  validateEditedRow,
}: UseTableEditingParams): UseTableEditingReturn {
  const buildWholeRowEditEntries = useCallback(
    (rowKey: string, row: Record<string, unknown>) => {
      const entries: Array<{ rowKey: string; initialValues: Record<string, unknown> }> = [
        { rowKey, initialValues: { ...row } },
      ];
      const subField = internalSchema.features.rows.subRows?.dataField;
      if (!subField) return entries;
      const subRows = row[subField] as unknown[] | undefined;
      const hasEditableSubCols = internalSchema.subRowColumns.some((c) => c.editable != null);
      if (!subRows || !hasEditableSubCols) return entries;
      subRows.forEach((sr, idx) => {
        entries.push({
          rowKey: `${rowKey}.${idx}`,
          initialValues: { ...(sr as Record<string, unknown>) },
        });
      });
      return entries;
    },
    [internalSchema.features.rows.subRows?.dataField, internalSchema.subRowColumns]
  );

  const getSubRowKeysForParent = useCallback(
    (parentKey: string) => {
      const keys: string[] = [];
      tableState.editingRows.forEach((_v, k) => {
        if (k.startsWith(`${parentKey}.`)) keys.push(k);
      });
      return keys;
    },
    [tableState.editingRows]
  );

  const handleCellDoubleClick = useCallback(
    (rowKey: string, _field: string) => {
      if (!tableState.editingRows.has(rowKey)) {
        const row = displayData.find((r, idx) => {
          const rec = r as Record<string, unknown>;
          return resolveRowKey(rec, idx) === rowKey;
        }) as Record<string, unknown> | undefined;
        if (row) {
          const entries = buildWholeRowEditEntries(rowKey, row);
          stateActions.startEditingBatch(entries);
        }
      }
    },
    [tableState.editingRows, displayData, resolveRowKey, stateActions, buildWholeRowEditEntries]
  );

  const handleEditChange = useCallback(
    (rowKey: string, field: string, value: unknown) => {
      stateActions.updateEditValue(rowKey, field, value);
      const currentEdits = tableState.editingRows.get(rowKey) ?? {};
      let updatedRow = { ...currentEdits, [field]: value };
      const computedRow = applyComputeRules(updatedRow, field);
      if (computedRow !== updatedRow) {
        const preComputeRow = updatedRow;
        updatedRow = computedRow;
        Object.entries(computedRow).forEach(([k, v]) => {
          if (k !== field && v !== preComputeRow[k]) {
            stateActions.updateEditValue(rowKey, k, v);
          }
        });
      }

      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === rowKey;
      }) as Record<string, unknown> | undefined;

      let mergedRow: Record<string, unknown> = original
        ? { ...original, ...updatedRow }
        : { ...updatedRow };

      if (patchEditedRow) {
        mergedRow = patchEditedRow(mergedRow, { field, value });
        Object.entries(mergedRow).forEach(([k, v]) => {
          const prior = original ? { ...original, ...currentEdits, [field]: value }[k] : currentEdits[k];
          if (v !== prior) {
            stateActions.updateEditValue(rowKey, k, v);
          }
        });
      }

      if (validateEditedRow && stateActions.setRowEditErrors) {
        stateActions.setRowEditErrors(rowKey, validateEditedRow(mergedRow));
      }

      const col = internalSchema.columns.find((c) => c.field === field);
      if (col && col.clearOnEdit.length > 0) {
        col.clearOnEdit.forEach((clearField) => {
          stateActions.updateEditValue(rowKey, clearField, null);
        });
      }
    },
    [
      stateActions,
      tableState.editingRows,
      applyComputeRules,
      internalSchema.columns,
      displayData,
      resolveRowKey,
      patchEditedRow,
      validateEditedRow,
    ]
  );

  const handleEditBlur = useCallback((_rowKey: string, _field: string) => {
    // Placeholder for future Yup validation on blur
  }, []);

  const subRowDataField = internalSchema.features.rows.subRows?.dataField;

  const handleSubRowCellDoubleClick = useCallback(
    (parentKey: string, _subIndex: number, _field: string) => {
      if (tableState.editingRows.has(parentKey)) return;

      const parentRow = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === parentKey;
      }) as Record<string, unknown> | undefined;

      if (!parentRow) return;

      const entries = buildWholeRowEditEntries(parentKey, parentRow);
      stateActions.startEditingBatch(entries);
    },
    [tableState.editingRows, displayData, resolveRowKey, stateActions, buildWholeRowEditEntries]
  );

  const handleSubRowEditChange = useCallback(
    (parentKey: string, subIndex: number, field: string, value: unknown) => {
      const subRowKey = `${parentKey}.${subIndex}`;
      stateActions.updateEditValue(subRowKey, field, value);

      const currentEdits = tableState.editingRows.get(subRowKey) ?? {};
      const updatedRow = { ...currentEdits, [field]: value };
      const computedRow = applyComputeRules(updatedRow, field);
      if (computedRow !== updatedRow) {
        Object.entries(computedRow).forEach(([k, v]) => {
          if (k !== field && v !== updatedRow[k]) {
            stateActions.updateEditValue(subRowKey, k, v);
          }
        });
      }

      if (validateEditedRow && stateActions.setRowEditErrors && subRowDataField) {
        const parentRow = displayData.find((r, idx) => {
          const rec = r as Record<string, unknown>;
          return resolveRowKey(rec, idx) === parentKey;
        }) as Record<string, unknown> | undefined;
        const originalSubRow = parentRow
          ? ((parentRow[subRowDataField] as unknown[] | undefined)?.[subIndex] as Record<string, unknown> | undefined)
          : undefined;
        const mergedSubRow = originalSubRow
          ? { ...originalSubRow, ...(computedRow !== updatedRow ? computedRow : updatedRow) }
          : (computedRow !== updatedRow ? computedRow : updatedRow);
        stateActions.setRowEditErrors(subRowKey, validateEditedRow(mergedSubRow));
      }

      const col = internalSchema.subRowColumns.find((c) => c.field === field);
      if (col && col.clearOnEdit.length > 0) {
        col.clearOnEdit.forEach((clearField) => {
          stateActions.updateEditValue(subRowKey, clearField, null);
        });
      }
    },
    [stateActions, tableState.editingRows, applyComputeRules, internalSchema.subRowColumns, validateEditedRow, subRowDataField, displayData, resolveRowKey]
  );

  const handleSubRowEditBlur = useCallback(
    (_parentKey: string, _subIndex: number, _field: string) => {
      // Placeholder for future Yup validation on blur
    },
    []
  );

  const editingSubRows = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    tableState.editingRows.forEach((values, key) => {
      if (key.includes('.')) {
        map.set(key, values);
      }
    });
    return map;
  }, [tableState.editingRows]);

  const subRowEditErrors = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    tableState.editErrors.forEach((errors, key) => {
      if (key.includes('.')) {
        map.set(key, errors);
      }
    });
    tableState.serverErrors.forEach((errors, key) => {
      if (key.includes('.')) {
        const existing = map.get(key) ?? {};
        map.set(key, { ...existing, ...errors });
      }
    });
    return map;
  }, [tableState.editErrors, tableState.serverErrors]);

  return {
    buildWholeRowEditEntries,
    getSubRowKeysForParent,
    handleCellDoubleClick,
    handleEditChange,
    handleEditBlur,
    subRowDataField,
    handleSubRowCellDoubleClick,
    handleSubRowEditChange,
    handleSubRowEditBlur,
    editingSubRows,
    subRowEditErrors,
  };
}
